import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useConfig, waLink } from "@/context/ConfigContext";
import { GoogleSignInButton, SignedInChip } from "@/components/GoogleAuth";
import { useVisitorAuth } from "@/context/VisitorAuthContext";
import { FILTER_OPTIONS } from "@/components/home/SearchFilter";

const inputClass =
  "w-full rounded-sm border border-gray-200 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/30";

export default function OfferForm() {
  const config = useConfig();
  const { visitor, loading } = useVisitorAuth();
  const [form, setForm] = useState({ name: "", mobile: "", budget: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (visitor) {
      setForm((f) => ({ ...f, name: f.name || visitor.name || "" }));
    }
  }, [visitor]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        mobile: form.mobile,
        budget: form.budget,
        property_title: "Kesar Riverdale & Mauli Nivasa (Special Offer)",
        message: "Special offer lead: requested free brochure, layout plan and a free site visit (80% bank loan offer).",
      });
      setDone(true);
      toast.success("Details received! We will contact you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const heading = (
    <>
      <p className="text-sm font-extrabold leading-snug text-[#B8962E]">
        80% Bank Loan for Kesar Riverdale &amp; Mauli Nivasa + Limited-Time Special Discount Offer!
      </p>
      <p className="mt-1.5 text-xs leading-relaxed text-navy/70">
        Enter your name and number &mdash; former BSF officer Sandeep ji will personally contact you to book a
        free site visit.
      </p>
    </>
  );

  if (done) {
    return (
      <div data-testid="offer-success" className="rounded-lg border border-gold/40 bg-white/95 p-6 shadow-2xl backdrop-blur">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-600" aria-hidden="true" />
          <div>
            <p className="font-display text-lg font-bold text-navy">Thank you, {form.name}!</p>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Your details have been received. Sandeep ji will personally contact you shortly to book your
              free site visit, and the brochure &amp; layout plan will be shared with you.
            </p>
            <a
              href={waLink(config, `Hello, I just submitted the special offer form (${form.name}). Please share the Kesar Riverdale & Mauli Nivasa brochure and layout plan.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="offer-whatsapp-button"
              className="mt-4 inline-flex items-center gap-2 rounded-sm bg-[#25D366] px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:scale-[1.03]"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Get Brochure on WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rounded-lg border border-gold/40 bg-white/95 p-6 shadow-2xl backdrop-blur">
        <p className="text-center text-sm font-semibold uppercase tracking-widest text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <form
      data-testid="offer-form"
      onSubmit={submit}
      className="rounded-lg border border-gold/40 bg-white/95 p-5 shadow-2xl backdrop-blur sm:p-6"
    >
      {visitor && <SignedInChip />}
      {heading}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input
          data-testid="offer-name-input"
          className={inputClass}
          placeholder="Your Name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <input
          data-testid="offer-mobile-input"
          className={inputClass}
          placeholder="Mobile Number"
          type="tel"
          value={form.mobile}
          onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
        />
      </div>
      <select
        data-testid="offer-budget-select"
        className={`${inputClass} mt-3`}
        value={form.budget}
        onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
      >
        <option value="">Your Budget (Optional)</option>
        {FILTER_OPTIONS.budget.map((b) => (
          <option key={b.value} value={b.label}>{b.label}</option>
        ))}
      </select>
      <button
        data-testid="offer-submit-button"
        type="submit"
        disabled={submitting}
        className="mt-3 w-full rounded-sm bg-gold px-6 py-3.5 text-sm font-extrabold uppercase tracking-wide text-navy transition-colors duration-200 hover:bg-gold-light disabled:opacity-60"
      >
        {submitting ? "Sending..." : "Get Special Offer + Free Brochure"}
      </button>
      {!visitor && (
        <div className="mt-4 border-t border-gray-200 pt-4" data-testid="offer-optional-signin">
          <GoogleSignInButton label="Sign in with Google (Optional)" />
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            No sign-in needed to claim the offer &mdash; it just speeds up follow-up.
          </p>
        </div>
      )}
    </form>
  );
}
